import moment from "moment";
import uniqWith from "lodash-es/uniqWith";
import isEqual from "lodash-es/isEqual";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

// API
import { getCommissionCycles } from "@/Common/api/commissionCycles";

// Interfaces
import { ICommissionCycle, IPayPeriod } from "@/Common/api/types/CommissionCycle";

// Helpers
import { errorLogger } from "@/Common/helpers/logger";
import { findPartnerPayPeriod } from "@/Common/helpers/PayPeriods";

type GetCommissionCycleArgs = Readonly<{
	allCommissionCycles: ICommissionCycle[];
	commissionCycles: ICommissionCycle[];
	commissionCycleId?: string;
	date?: string | Date;
}>;

const getCommissionCycle = ({ allCommissionCycles, commissionCycles, commissionCycleId, date }: GetCommissionCycleArgs) => {
	if (date) {
		const commissionCycle = allCommissionCycles.find((cycle) => moment(date).isBetween(cycle.startDate, cycle.endDate, "days", "[]"));
		if (commissionCycle) {
			return commissionCycle;
		}
	}

	if (!commissionCycleId) {
		return commissionCycles?.[0];
	}
	return allCommissionCycles.find((cycle) => cycle._id === commissionCycleId);
};

const getPayPeriods = (commissionCycles: ICommissionCycle[]) => {
	return commissionCycles.flatMap((cycle) => cycle.payPeriods).filter((period) => period.status !== "COMPLETED");
};

const getPayPeriod = (commissionCycle?: ICommissionCycle, payPeriodId?: string) => {
	if (!payPeriodId && !commissionCycle?.payPeriods?.length) {
		return undefined;
	}
	if (!payPeriodId) {
		return commissionCycle?.payPeriods
			.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
			.find((payPeriod) => payPeriod.status !== "COMPLETED");
	}
	return commissionCycle?.payPeriods.find((period) => period._id === payPeriodId);
};

interface IUseCommissionCyclesDataArgs {
	commissionCycleId?: string;
	payPeriodId?: string;
	date?: string | Date;
}

const resolveCompletePositionStatus = (payPeriod?: IPayPeriod, partnerPayPeriod?: IPayPeriod | null, ccId = "") => {
	const result = [];
	const positionStatus = payPeriod?.positionStatus ?? [];
	for (const element of positionStatus) {
		result.push({ position: element.position, status: element.status, ccId });
	}
	const partnerCCId = payPeriod?.nextCCId ?? payPeriod?.previousCCId;
	const partnerPositionStatus = partnerPayPeriod?.positionStatus ?? [];
	for (const element of partnerPositionStatus) {
		result.push({ position: element.position, status: element.status, ccId: partnerCCId });
	}
	return uniqWith(result, isEqual);
};

const getPayPeriodByDate = (commissionCycle?: ICommissionCycle, date?: Date) => {
	return commissionCycle?.payPeriods.find((period) => moment(date).isBetween(period.startDate, period.endDate, "days", "[]"));
};

export const useCommissionCyclesData = (args?: Readonly<IUseCommissionCyclesDataArgs>) => {
	const { commissionCycleId, payPeriodId, date } = args ?? {};

	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId ?? "";

	const { data: allCommissionCycles = [], isLoading } = useQuery({
		queryKey: ["commissionCycles", locationId],
		queryFn: async () => {
			const result = await getCommissionCycles(locationId);
			if (!result) {
				errorLogger("Something went wrong while fetching commission cycles data");
				return [];
			}
			return result;
		},
		staleTime: Infinity,
		enabled: Boolean(locationId),
	});

	const commissionCycles = allCommissionCycles.filter((cycle) => !["STAGING", "FINALIZED"].includes(cycle.status));
	const commissionCycle = getCommissionCycle({ allCommissionCycles, commissionCycles, commissionCycleId, date });
	const payPeriods = getPayPeriods(commissionCycles);
	const payPeriod = getPayPeriod(commissionCycle, payPeriodId);
	const payPeriodByDate = getPayPeriodByDate(commissionCycle, date as Date);
	const partnerPayPeriod = findPartnerPayPeriod({ commissionCycles: allCommissionCycles, payPeriod });
	const positionStatus = payPeriod?.positionStatus ?? [];
	const positionStatusPayPeriodParts = resolveCompletePositionStatus(payPeriod, partnerPayPeriod, commissionCycle?._id);
	return {
		isLoading,
		locationId,
		allCommissionCycles,
		commissionCycles,
		commissionCycle,
		payPeriods,
		payPeriod,
		payPeriodByDate,
		partnerPayPeriod,
		positionStatus,
		positionStatusPayPeriodParts,
	};
};
